import { yupResolver } from "@hookform/resolvers/yup";
import {
  Button,
  Checkbox,
  Col,
  Form,
  Input,
  Row,
  Select,
  Space,
  Switch,
} from "antd";
import { has } from "lodash-es";
import { observer } from "mobx-react-lite";
import { FC } from "react";
import { Controller, useForm } from "react-hook-form";
import { useIntl } from "react-intl";

import ErrorsFormatter from "@/ui/_common_/errors-formatter";
import { FormSpinner } from "@/ui/_common_/form-spinner";

import { FormState } from "./FormState";
import { ISchema, useSchema } from "./schema";

type Props = {
  state: FormState;
};

export const FormView: FC<Props> = observer(({ state }) => {
  const intl = useIntl();
  const _ = intl.formatMessage;
  const schema = useSchema();
  const resolver = yupResolver(schema);
  const form = useForm<ISchema>({ resolver });

  if (
    state.articleQuery.isIdle ||
    state.articleQuery.isPending ||
    state.categoriesQuery.isIdle ||
    state.categoriesQuery.isPending
  ) {
    return <FormSpinner />;
  }

  if (state.articleQuery.isRejected || state.categoriesQuery.isRejected) {
    return (
      <ErrorsFormatter queries={[state.articleQuery, state.categoriesQuery]} />
    );
  }

  return (
    <Space direction="vertical">
      <ErrorsFormatter queries={[state.updateQuery]} />
      <Form
        labelCol={{ span: 6 }}
        labelAlign="left"
        wrapperCol={{ span: 18 }}
        layout="horizontal"
        onFinish={form.handleSubmit(state.handleSubmit)}
      >
        <Form.Item
          label={intl.formatMessage({ defaultMessage: "Category" })}
          validateStatus={
            has(form.formState.errors, "categoryId") ? "error" : ""
          }
          help={form.formState.errors.categoryId?.message}
        >
          <Controller
            control={form.control}
            name="categoryId"
            defaultValue={state.article.category.id}
            render={({ field }) => (
              <Select
                {...field}
                placeholder={intl.formatMessage({
                  defaultMessage: "Select category",
                })}
              >
                {state.categories.map((category) => (
                  <Select.Option key={category.id} value={category.id}>
                    {category.name}
                  </Select.Option>
                ))}
              </Select>
            )}
          />
        </Form.Item>
        <Form.Item
          label={intl.formatMessage({ defaultMessage: "Title" })}
          validateStatus={has(form.formState.errors, "name") ? "error" : ""}
          help={form.formState.errors.name?.message}
        >
          <Controller
            control={form.control}
            name="name"
            defaultValue={state.article.name}
            render={({ field }) => (
              <Input
                {...field}
                placeholder={intl.formatMessage({
                  defaultMessage: "Enter title",
                })}
              />
            )}
          />
        </Form.Item>
        <Form.Item
          label={intl.formatMessage({ defaultMessage: "Slug" })}
          validateStatus={has(form.formState.errors, "slug") ? "error" : ""}
          help={form.formState.errors.slug?.message}
        >
          <Controller
            control={form.control}
            name="slug"
            defaultValue={state.article.slug}
            render={({ field }) => (
              <Input
                {...field}
                placeholder={intl.formatMessage({
                  defaultMessage: "Enter slug",
                })}
              />
            )}
          />
        </Form.Item>
        <Form.Item
          label={intl.formatMessage({ defaultMessage: "File path" })}
          validateStatus={has(form.formState.errors, "filePath") ? "error" : ""}
          help={form.formState.errors.filePath?.message}
        >
          <Controller
            control={form.control}
            name="filePath"
            defaultValue={state.article.filePath}
            render={({ field }) => (
              <Input
                {...field}
                placeholder={intl.formatMessage({
                  defaultMessage: "Enter file path",
                })}
              />
            )}
          />
        </Form.Item>
        <Form.Item
          label={intl.formatMessage({ defaultMessage: "Access" })}
          validateStatus={
            has(form.formState.errors, "accessControlList") ? "error" : ""
          }
        >
          <Controller
            control={form.control}
            name="accessControlList"
            defaultValue={state.acl}
            render={({ field }) => (
              <Row gutter={[8, 8]} wrap={true}>
                {field.value.slice(3).map((entry) => (
                  <Col key={entry.role} xs={24}>
                    <Space direction="horizontal">
                      <Checkbox
                        checked={entry.permissions.includes("Read")}
                        onChange={(event) => {
                          const isChecked = event.target.checked;
                          if (isChecked) {
                            entry.permissions.push("Read");
                          } else {
                            entry.permissions = [];
                          }
                          field.onChange([...field.value]);
                        }}
                      >
                        {_({ id: `role/${entry.role}` })}
                      </Checkbox>
                    </Space>
                  </Col>
                ))}
              </Row>
            )}
          />
        </Form.Item>
        <Form.Item
          label={intl.formatMessage({ defaultMessage: "Enabled" })}
          validateStatus={
            has(form.formState.errors, "isEnabled") ? "error" : ""
          }
          help={form.formState.errors.isEnabled?.message}
        >
          <Controller
            control={form.control}
            name="isEnabled"
            defaultValue={state.article.isEnabled}
            render={({ field }) => <Switch {...field} checked={field.value} />}
          />
        </Form.Item>
        <Row justify="end" gutter={12}>
          <Col>
            <Button
              type="primary"
              htmlType="submit"
              loading={state.updateQuery.isPending}
            >
              {intl.formatMessage({ defaultMessage: "Update" })}
            </Button>
          </Col>
        </Row>
      </Form>
    </Space>
  );
});
